import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  LinearScale,
  CategoryScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js"
import "chartjs-adapter-date-fns"
import { nl } from "date-fns/locale"

// Register Chart.js components
Chart.register(
  LineController,
  BarController,
  DoughnutController,
  LineElement,
  BarElement,
  ArcElement,
  PointElement,
  LinearScale,
  CategoryScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  Filler
)

// Ochtendlicht-pijlerkleuren (zie application.tailwind.css)
const colors = {
  primary: { primary: "rgb(15, 118, 110)", light: "rgba(15, 118, 110, 0.08)" },
  water: { primary: "rgb(30, 155, 215)", light: "rgba(30, 155, 215, 0.08)" },
  sleep: { primary: "rgb(123, 108, 240)", light: "rgba(123, 108, 240, 0.08)" },
  food: { primary: "rgb(232, 150, 46)", light: "rgba(232, 150, 46, 0.08)" },
  routine: { primary: "rgb(229, 86, 138)", light: "rgba(229, 86, 138, 0.08)" },
  move: { primary: "rgb(16, 163, 127)", light: "rgba(16, 163, 127, 0.08)" }
}

const activityColors = {
  running: "#10A37F",
  walking: "#0F766E",
  cycling: "#E8962E",
  swimming: "#1E9BD7",
  strength: "#E5568A",
  yoga: "#7B6CF0",
  hiking: "#E2B714",
  other: "#9AAEAC"
}

const INK = "#0d2524"
const MUTED = "#4e6463"
const FONT = "'Instrument Sans', ui-sans-serif, system-ui, sans-serif"

Chart.defaults.font.family = FONT
Chart.defaults.color = MUTED

export default class extends Controller {
  static values = {
    type: { type: String, default: "line" },
    data: Object,
    options: { type: Object, default: {} },
    sparkline: { type: Boolean, default: false }
  }

  connect() {
    this.createChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  dataValueChanged() {
    if (this.chart) {
      this.chart.data = this.dataValue
      this.chart.update()
    }
  }

  createChart() {
    const ctx = this.element.getContext("2d")
    const config = this.buildConfig()
    this.chart = new Chart(ctx, config)
  }

  buildConfig() {
    const baseOptions = this.sparklineValue ? this.sparklineOptions() : this.defaultOptions()
    const mergedOptions = this.deepMerge(baseOptions, this.optionsValue)

    return {
      type: this.typeValue,
      data: this.processData(this.dataValue),
      options: mergedOptions
    }
  }

  processData(data) {
    // Add colors to datasets if not provided
    if (data.datasets) {
      data.datasets = data.datasets.map((dataset, index) => {
        const colorKeys = Object.keys(colors)
        const colorKey = colorKeys[index % colorKeys.length]
        const color = colors[colorKey]

        const defaults = {
          borderColor: color.primary,
          backgroundColor: this.typeValue === "line" ? color.light : color.primary,
          pointBackgroundColor: color.primary,
          pointRadius: this.typeValue === "line" ? 2.5 : undefined,
          pointHoverRadius: 5,
          borderWidth: this.typeValue === "line" ? 2.5 : 0,
          borderRadius: this.typeValue === "bar" ? 6 : undefined,
          tension: 0.35,
          fill: this.typeValue === "line"
        }

        return { ...defaults, ...dataset }
      })
    }

    return data
  }

  defaultOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: "index"
      },
      plugins: {
        legend: {
          display: true,
          position: "top",
          align: "end",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            padding: 16,
            font: {
              size: 12
            }
          }
        },
        tooltip: {
          backgroundColor: INK,
          titleFont: { size: 13, weight: "600" },
          bodyFont: { size: 12 },
          padding: 12,
          cornerRadius: 12,
          boxPadding: 4,
          displayColors: true
        }
      },
      scales: this.typeValue === "doughnut" ? {} : {
        x: {
          border: { display: false },
          grid: {
            display: false
          },
          ticks: {
            font: { size: 11 },
            color: MUTED
          }
        },
        y: {
          border: { display: false },
          grid: {
            color: "rgba(13, 37, 36, 0.06)"
          },
          ticks: {
            font: { size: 11 },
            color: MUTED
          },
          beginAtZero: false
        }
      },
      adapters: {
        date: {
          locale: nl
        }
      }
    }
  }

  sparklineOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      },
      scales: {
        x: { display: false },
        y: { display: false }
      },
      elements: {
        point: { radius: 0 },
        line: { borderWidth: 2 }
      }
    }
  }

  deepMerge(target, source) {
    const result = { ...target }
    for (const key in source) {
      if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(result[key] || {}, source[key])
      } else {
        result[key] = source[key]
      }
    }
    return result
  }
}

// Export colors for use in components
export { colors, activityColors }
